import { Suspense, useRef, useEffect, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Environment, PerspectiveCamera } from "@react-three/drei";
import * as THREE from "three";
import { AlphabetGrid } from "../components/AlphabetGrid";
import { StarReward } from "../../../../components/StarReward";
import { ObjectModel } from "../../../../components/ObjectModel";
import { Avatar3D } from "../../../../components/Avatar3D";
import { LearningItem } from "../../../../data/types";
import { AvatarType } from "../../../../components/AvatarSelector";
import { useAvatarControls } from "../../../../hooks/useAvatarControls";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { playWalkingSound, stopWalkingSound } from "../../../../utils/audio";
import type { SceneThemeId } from "../AlphabetFinderGame";

/** Red warning circle indicator that appears below avatar when follower is near */
function WarningCircle({ position, intensity }: { position: [number, number, number]; intensity: number }) {
  const circleRef = useRef<THREE.Mesh>(null);
  const pulseRef = useRef(0);

  useFrame(() => {
    if (circleRef.current) {
      // Pulsing animation - faster when intensity is higher
      pulseRef.current += 0.1 + (intensity * 0.15);
      const scale = 1 + Math.sin(pulseRef.current) * 0.2 * intensity;
      circleRef.current.scale.set(scale, scale, 1);
      
      // Opacity based on intensity
      const material = circleRef.current.material as THREE.MeshStandardMaterial;
      material.opacity = 0.3 + (intensity * 0.7);
      material.emissiveIntensity = intensity * 2;
    }
  });

  return (
    <mesh ref={circleRef} position={position} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <ringGeometry args={[0.8, 1.2, 32]} />
      <meshStandardMaterial
        color="#ff0000"
        emissive="#ff0000"
        transparent
        opacity={0.5}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

/** Drei Environment preset names (subset we use). */
type EnvPreset = "sunset" | "dawn" | "park" | "forest" | "apartment" | "studio" | "warehouse" | "night";

/** Theme-specific colors and lighting; boxes stay the same. envPreset overrides HDRI when theme has no built-in preset. */
export const THEME_CONFIG: Record<
  SceneThemeId,
  { 
    groundColor: string; 
    skyColor: string; 
    skyGradient?: { from: string; to: string };
    ambientIntensity: number; 
    directionalIntensity: number; 
    pointLightColor: string; 
    pointLightIntensity: number;
    envPreset?: EnvPreset; // use when theme id is not a valid drei preset (e.g. jungle -> forest, beach -> sunset)
  }
> = {
  sunset: { 
    groundColor: "#fef3e2", 
    skyColor: "#ffd4a3",
    skyGradient: { from: "#ffb347", to: "#ff8c69" },
    ambientIntensity: 0.65, 
    directionalIntensity: 1, 
    pointLightColor: "#ffaa66", 
    pointLightIntensity: 0.6 
  },
  dawn: { 
    groundColor: "#f5e6ff", 
    skyColor: "#e6d4ff",
    skyGradient: { from: "#d4b3ff", to: "#c299ff" },
    ambientIntensity: 0.6, 
    directionalIntensity: 0.9, 
    pointLightColor: "#e8c4ff", 
    pointLightIntensity: 0.5 
  },
  park: { 
    groundColor: "#e8f5e9", 
    skyColor: "#b8e6c1",
    skyGradient: { from: "#a5d6a7", to: "#81c784" },
    ambientIntensity: 0.7, 
    directionalIntensity: 1.1, 
    pointLightColor: "#a5d6a7", 
    pointLightIntensity: 0.4 
  },
  forest: { 
    groundColor: "#e8e0d5", 
    skyColor: "#d4c4b0",
    skyGradient: { from: "#c8b896", to: "#a09070" },
    ambientIntensity: 0.55, 
    directionalIntensity: 0.85, 
    pointLightColor: "#c8b896", 
    pointLightIntensity: 0.5 
  },
  jungle: { 
    groundColor: "#2d4a2d", 
    skyColor: "#1a3d1a",
    skyGradient: { from: "#0d2810", to: "#2d5a2d" },
    ambientIntensity: 0.5, 
    directionalIntensity: 0.75, 
    pointLightColor: "#6b8e6b", 
    pointLightIntensity: 0.55,
    envPreset: "forest"
  },
  beach: { 
    groundColor: "#e8dcc8", 
    skyColor: "#87ceeb",
    skyGradient: { from: "#b0e0e6", to: "#ffd89b" },
    ambientIntensity: 0.75, 
    directionalIntensity: 1.1, 
    pointLightColor: "#fff5e1", 
    pointLightIntensity: 0.6,
    envPreset: "sunset"
  },
  apartment: { 
    groundColor: "#f5f5dc", 
    skyColor: "#fffef0",
    skyGradient: { from: "#fff8e7", to: "#fffacd" },
    ambientIntensity: 0.7, 
    directionalIntensity: 1, 
    pointLightColor: "#fff8e7", 
    pointLightIntensity: 0.5 
  },
  studio: { 
    groundColor: "#ececec", 
    skyColor: "#f5f5f5",
    skyGradient: { from: "#ffffff", to: "#e0e0e0" },
    ambientIntensity: 0.75, 
    directionalIntensity: 1.2, 
    pointLightColor: "#ffffff", 
    pointLightIntensity: 0.45 
  },
  warehouse: { 
    groundColor: "#e0e4e8", 
    skyColor: "#d0d8e0",
    skyGradient: { from: "#b0c4de", to: "#87ceeb" },
    ambientIntensity: 0.6, 
    directionalIntensity: 1, 
    pointLightColor: "#b0c4de", 
    pointLightIntensity: 0.5 
  },
  night: { 
    groundColor: "#2a2a3a", 
    skyColor: "#1a1a2e",
    skyGradient: { from: "#0f0f1e", to: "#2a2a3a" },
    ambientIntensity: 0.35, 
    directionalIntensity: 0.5, 
    pointLightColor: "#8899cc", 
    pointLightIntensity: 0.7 
  },
};

interface AlphabetStreetProps {
  sceneTheme?: SceneThemeId;
  items: LearningItem[];
  onItemClick: (item: LearningItem, position: [number, number, number]) => void;
  showStar: boolean;
  starPosition: [number, number, number] | null;
  onStarComplete: () => void;
  showObject: boolean;
  objectPosition: [number, number, number] | null;
  objectModelPath: string | null;
  avatarType: AvatarType | null;
  avatarTarget: [number, number, number] | null;
  onAvatarReachTarget: () => void;
  completedItems: string[];
  avatarPosition?: [number, number, number]; // Initial position (optional, will be managed internally)
  onBoxPositionsUpdate?: (positions: Map<string, [number, number, number]>) => void;
  shuffleKey?: number;
  onAvatarPositionChange?: (position: [number, number, number]) => void; // Callback for position updates
  currentMode?: string; // For collision detection
  boxPositions?: Map<string, [number, number, number]>; // For collision detection
  completedItemsForCollision?: string[]; // For collision detection to skip completed items
  // Avatar controls from parent
  direction?: "up" | "down" | "left" | "right" | null;
  rotationDirection?: "left" | "right" | "down" | null;
  isMoving?: boolean;
  // Optional touch gesture handlers (for external control integration)
  onTouchGesture?: (action: "up" | "down" | "left" | "right") => void;
  // Follower/chaser mechanic
  onGameOver?: () => void; // Called when follower catches avatar
  enableFollower?: boolean; // Enable/disable follower
  onFollowerDistanceChange?: (distance: number) => void; // Report distance for warning UI
  isGameActive?: boolean; // Whether game is active (not in overlay)
  onFollowerPositionChange?: (position: [number, number, number]) => void; // Report follower position for minimap
  onAvatarRotationChange?: (rotation: number) => void; // Report avatar rotation for minimap
  // Hint system
  hintedItemId?: string | null; // ID of the item currently being hinted
  isHintActive?: boolean; // Whether hint is currently active
}

export function AlphabetStreet({
  sceneTheme = "sunset",
  items,
  onItemClick,
  showStar,
  starPosition,
  onStarComplete,
  showObject,
  objectPosition,
  objectModelPath,
  avatarType,
  avatarTarget,
  onAvatarReachTarget,
  completedItems,
  avatarPosition: initialAvatarPosition = [0, 0, -20], // Start outside front of grid
  onBoxPositionsUpdate,
  shuffleKey = 0,
  onAvatarPositionChange,
  currentMode = "alphabet",
  boxPositions = new Map(),
  completedItemsForCollision = [],
  direction: externalDirection = null,
  rotationDirection: externalRotationDirection = null,
  isMoving: externalIsMoving = false,
  onTouchGesture,
  onGameOver,
  enableFollower = true,
  onFollowerDistanceChange,
  isGameActive = true,
  onFollowerPositionChange,
  onAvatarRotationChange,
  hintedItemId = null,
  isHintActive = false,
}: AlphabetStreetProps) {
  const themeConfig = THEME_CONFIG[sceneTheme];
  // Navigation management - use external controls if provided, otherwise use internal hook
  const internalControls = useAvatarControls();
  const direction = externalDirection !== null ? externalDirection : internalControls.direction;
  const rotationDirection = externalRotationDirection !== null ? externalRotationDirection : internalControls.rotationDirection;
  const isMoving = externalIsMoving !== false ? externalIsMoving : internalControls.isMoving;
  const isMobile = useIsMobile();
  
  // Internal state for avatar position and rotation
  const [internalAvatarPosition, setInternalAvatarPosition] = useState<[number, number, number]>(initialAvatarPosition);
  const [internalAvatarRotation, setInternalAvatarRotation] = useState<number>(0);
  const [internalIsAvatarMoving, setInternalIsAvatarMoving] = useState(false);

  // Touch gesture state for swipe detection
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const touchStartTime = useRef<number>(0);
  const isGestureActive = useRef<boolean>(false);
  const gestureDirection = useRef<"up" | "down" | "left" | "right" | null>(null);
  const isTouchHolding = useRef<boolean>(false);
  const touchHoldInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  // Use internal state for avatar position and rotation
  const avatarPosition = internalAvatarPosition;
  const avatarRotation = internalAvatarRotation;
  const isAvatarMoving = internalIsAvatarMoving;

  // Follower/chaser state - starts far behind avatar
  const [followerPosition, setFollowerPosition] = useState<[number, number, number]>([0, 0, -30]);
  const [followerRotation, setFollowerRotation] = useState<number>(0);
  const followerSpeed = useRef(0.08); // Base speed - slightly slower than avatar
  const [followerDistance, setFollowerDistance] = useState<number>(100); // Track distance for indicator

  // Navigation Management Functions - Natural avatar navigation
  // Left/Right/Down: Rotate avatar direction AND set camera behind avatar immediately
  // Up: Move forward in facing direction

  useEffect(() => {
    if (!rotationDirection) {
      return;
    }

    // Handle rotation keys - set avatar direction and camera position immediately
    if (rotationDirection === "down") {
      // Down: rotate 180 degrees (turn around)
      setInternalAvatarRotation(prev => {
        const normalized = ((prev % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const newRotation = normalized + Math.PI;
        // Camera will update automatically via useEffect dependency on avatarRotation
        return newRotation;
      });
      return;
    } else if (rotationDirection === "right") {
      // Right: rotate 90 degrees counter-clockwise
      setInternalAvatarRotation(prev => {
        const normalized = ((prev % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const newRotation = normalized - Math.PI / 2;
        // Camera will update automatically via useEffect dependency on avatarRotation
        return newRotation;
      });
      return;
    } else if (rotationDirection === "left") {
      // Left: rotate 90 degrees clockwise
      setInternalAvatarRotation(prev => {
        const normalized = ((prev % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const newRotation = normalized + Math.PI / 2;
        // Camera will update automatically via useEffect dependency on avatarRotation
        return newRotation;
      });
      return;
    }
    // Up key doesn't change rotation (only moves forward)
  }, [rotationDirection]);

  // Handle avatar forward movement with up key - moves in direction avatar is facing
  useEffect(() => {
    const wasMoving = internalIsAvatarMoving;
    setInternalIsAvatarMoving(isMoving && direction === "up");

    // Play/stop walking sound
    if (isMoving && direction === "up" && !wasMoving) {
      playWalkingSound();
    } else if ((!isMoving || direction !== "up") && wasMoving) {
      stopWalkingSound();
    }

    if (direction !== "up" || !isMoving) {
      return;
    }

    const interval = setInterval(() => {
      setInternalAvatarPosition(prev => {
        let [x, y, z] = prev;
        const moveSpeed = 0.2;

        // Calculate forward movement based on avatar's rotation
        // Avatar rotation angle: 0 = facing +Z (forward), PI/2 = facing +X (right), etc.
        let newX = x + Math.sin(internalAvatarRotation) * moveSpeed;
        let newZ = z + Math.cos(internalAvatarRotation) * moveSpeed;

        // Collision detection with alphabet boxes (only in alphabet mode)
        if (currentMode === "alphabet" && boxPositions.size > 0) {
          const boxSize = 3.5; // Box is 3.5x3.5 units (updated for larger boxes)
          const avatarRadius = 0.3; // Avatar collision radius
          const minDistance = boxSize / 2 + avatarRadius;

          let canMove = true;
          boxPositions.forEach(([boxX, , boxZ], itemId) => {
            // Skip collision check for completed items
            if (completedItemsForCollision.includes(itemId)) return;

            const distance = Math.sqrt(
              Math.pow(newX - boxX, 2) + Math.pow(newZ - boxZ, 2)
            );

            if (distance < minDistance) {
              canMove = false;
            }
          });

          if (!canMove) {
            // Can't move, keep previous position
            return prev;
          }
        }

        // Keep avatar within bounds (calculated based on grid size and spacing)
        // For 6x6 grid with spacing of 9: max position is (5 - 2.5) * 9 = 22.5
        // Add buffer for avatar movement around boxes (box size 3.5 + some margin)
        if (currentMode === "alphabet") {
          const gridSize = 6;
          const spacing = 9;
          const boxSize = 3.5;
          const buffer = boxSize / 2 + 2; // Allow movement around boxes but limit going too far
          
          // Calculate actual grid bounds
          const maxRow = gridSize - 1;
          const maxCol = gridSize - 1;
          const maxX = (maxCol - (gridSize - 1) / 2) * spacing + buffer;
          const maxZ = (maxRow - (gridSize - 1) / 2) * spacing + buffer;
          const minX = -maxX;
          const minZ = -maxZ;
          
          newX = Math.max(minX, Math.min(maxX, newX));
          newZ = Math.max(minZ, Math.min(maxZ, newZ));
        } else {
          // For other modes, use default bounds
          const gridBounds = 15;
          newX = Math.max(-gridBounds, Math.min(gridBounds, newX));
          newZ = Math.max(-gridBounds, Math.min(gridBounds, newZ));
        }

        const newPosition: [number, number, number] = [newX, y, newZ];

        return newPosition;
      });
    }, 16); // ~60fps

    return () => clearInterval(interval);
  }, [direction, isMoving, internalAvatarRotation, currentMode, boxPositions, onAvatarPositionChange, completedItemsForCollision]);

  // Notify parent of position changes (useEffect to avoid setState during render)
  useEffect(() => {
    if (onAvatarPositionChange) {
      onAvatarPositionChange(internalAvatarPosition);
    }
  }, [internalAvatarPosition, onAvatarPositionChange]);

  // Notify parent of avatar rotation changes for minimap
  useEffect(() => {
    if (onAvatarRotationChange) {
      onAvatarRotationChange(internalAvatarRotation);
    }
  }, [internalAvatarRotation, onAvatarRotationChange]);

  // Reset position when shuffleKey changes (game reset)
  useEffect(() => {
    setInternalAvatarPosition([0, 0, -20]);
    setInternalAvatarRotation(0);
    setFollowerPosition([0, 0, -30]); // Reset follower far behind
    followerSpeed.current = 0.08; // Reset speed
  }, [shuffleKey]);

  // Follower movement - chases avatar
  useEffect(() => {
    if (!enableFollower || !isGameActive) return; // Don't move follower during round overlay

    const interval = setInterval(() => {
      setFollowerPosition(prev => {
        const [fx, fy, fz] = prev;
        const [ax, , az] = avatarPosition;

        // Calculate direction to avatar
        const dx = ax - fx;
        const dz = az - fz;
        const distance = Math.sqrt(dx * dx + dz * dz);

        // Update local distance state for indicator
        setFollowerDistance(distance);

        // Report distance to parent for warning UI
        if (onFollowerDistanceChange) {
          onFollowerDistanceChange(distance);
        }

        // Check if caught (distance < 1.5 units)
        if (distance < 1.5 && onGameOver) {
          onGameOver();
          return prev; // Stop movement
        }

        // Update follower rotation to face avatar
        if (distance > 0.1) {
          const targetRotation = Math.atan2(dx, dz);
          setFollowerRotation(targetRotation);
        }

        // Move follower toward avatar
        if (distance > 0.1) {
          const moveSpeed = followerSpeed.current;
          // Gradually increase speed as game progresses (based on completed items)
          const speedMultiplier = 1 + (completedItems.length * 0.01); // Gets faster as more letters found
          const adjustedSpeed = moveSpeed * speedMultiplier;

          const normalizedDx = dx / distance;
          const normalizedDz = dz / distance;

          let newFx = fx + normalizedDx * adjustedSpeed;
          let newFz = fz + normalizedDz * adjustedSpeed;

          // Keep follower within bounds (same as avatar)
          if (currentMode === "alphabet") {
            const gridSize = 6;
            const spacing = 9;
            const boxSize = 3.5;
            const buffer = boxSize / 2 + 2;
            const maxRow = gridSize - 1;
            const maxCol = gridSize - 1;
            const maxX = (maxCol - (gridSize - 1) / 2) * spacing + buffer;
            const maxZ = (maxRow - (gridSize - 1) / 2) * spacing + buffer;
            const minX = -maxX;
            const minZ = -maxZ;
            newFx = Math.max(minX, Math.min(maxX, newFx));
            newFz = Math.max(minZ, Math.min(maxZ, newFz));
          }

          return [newFx, fy, newFz];
        }

        return prev;
      });
    }, 16); // ~60fps

    return () => clearInterval(interval);
  }, [avatarPosition, enableFollower, onGameOver, currentMode, completedItems.length, isGameActive, onFollowerDistanceChange]);

  // Notify parent of follower position changes for minimap
  useEffect(() => {
    if (onFollowerPositionChange) {
      // Use queueMicrotask to avoid setState during render
      queueMicrotask(() => {
        onFollowerPositionChange(followerPosition);
      });
    }
  }, [followerPosition, onFollowerPositionChange]);

  // Touch gesture handlers for mobile swipe navigation
  // Touch and hold = continuous forward movement
  // Quick swipe left/right = change direction
  useEffect(() => {
    if (!isMobile) return;

    const stopForwardMovement = () => {
      if (touchHoldInterval.current) {
        clearInterval(touchHoldInterval.current);
        touchHoldInterval.current = null;
      }
      
      if (isTouchHolding.current) {
        isTouchHolding.current = false;
        
        // Stop movement
        if (onTouchGesture) {
          // For external controls, we'll handle stopping via the parent's touch end
          // The parent component should handle this
        } else if (internalControls.handleTouchEnd && externalDirection === null) {
          internalControls.handleTouchEnd("up");
        } else {
          setInternalIsAvatarMoving(false);
        }
      }
    };

    const startForwardMovement = () => {
      if (isTouchHolding.current) return; // Already moving
      
      isTouchHolding.current = true;
      
      if (onTouchGesture) {
        // Use callback - parent will handle continuous movement
        onTouchGesture("up");
      } else if (internalControls.handleTouchStart && externalDirection === null) {
        internalControls.handleTouchStart("up");
      } else {
        setInternalIsAvatarMoving(true);
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      // Don't interfere with UI button touches or if touching a clickable element
      const target = e.target as HTMLElement;
      if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
        return;
      }

      const touch = e.touches[0];
      if (touch) {
        touchStartPos.current = { x: touch.clientX, y: touch.clientY };
        touchStartTime.current = Date.now();
        isGestureActive.current = true;
        gestureDirection.current = null;
        isTouchHolding.current = false;
        
        // Start a timer to detect touch and hold (for forward movement)
        // If user holds for more than 150ms without significant movement, start moving forward
        setTimeout(() => {
          if (isGestureActive.current && !gestureDirection.current) {
            // No significant swipe detected, treat as touch and hold
            startForwardMovement();
          }
        }, 150);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isGestureActive.current || !touchStartPos.current) return;

      const touch = e.touches[0];
      if (!touch) return;

      const deltaX = touch.clientX - touchStartPos.current.x;
      const deltaY = touch.clientY - touchStartPos.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Minimum swipe distance (in pixels) to register as a swipe gesture
      const minSwipeDistance = 40;

      if (distance > minSwipeDistance) {
        // Stop forward movement if we detect a swipe
        stopForwardMovement();
        
        // Determine swipe direction based on which axis has more movement
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          // Horizontal swipe - change direction immediately
          gestureDirection.current = deltaX > 0 ? "right" : "left";
          
          // Handle direction change immediately
          const swipeDirection = gestureDirection.current;
          if (swipeDirection === "left") {
            // Turn left (90 degrees clockwise)
            if (onTouchGesture) {
              onTouchGesture("left");
            } else if (internalControls.handleTouchStart && externalRotationDirection === null) {
              internalControls.handleTouchStart("left");
              setTimeout(() => {
                if (internalControls.handleTouchEnd) {
                  internalControls.handleTouchEnd("left");
                }
              }, 50);
            } else {
              setInternalAvatarRotation(prev => {
                const normalized = ((prev % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
                return normalized + Math.PI / 2;
              });
            }
          } else if (swipeDirection === "right") {
            // Turn right (90 degrees counter-clockwise)
            if (onTouchGesture) {
              onTouchGesture("right");
            } else if (internalControls.handleTouchStart && externalRotationDirection === null) {
              internalControls.handleTouchStart("right");
              setTimeout(() => {
                if (internalControls.handleTouchEnd) {
                  internalControls.handleTouchEnd("right");
                }
              }, 50);
            } else {
              setInternalAvatarRotation(prev => {
                const normalized = ((prev % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
                return normalized - Math.PI / 2;
              });
            }
          }
        } else {
          // Vertical swipe
          if (deltaY < 0) {
            // Swipe up - could be forward movement, but we'll handle it on touch end
            gestureDirection.current = "up";
          } else {
            // Swipe down - turn around
            gestureDirection.current = "down";
            if (onTouchGesture) {
              onTouchGesture("down");
            } else if (internalControls.handleTouchStart && externalRotationDirection === null) {
              internalControls.handleTouchStart("down");
              setTimeout(() => {
                if (internalControls.handleTouchEnd) {
                  internalControls.handleTouchEnd("down");
                }
              }, 50);
            } else {
              setInternalAvatarRotation(prev => {
                const normalized = ((prev % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
                return normalized + Math.PI;
              });
            }
          }
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isGestureActive.current) return;

      const touchTime = Date.now() - touchStartTime.current;
      const wasHolding = isTouchHolding.current;

      // Stop forward movement if it was active (touch and hold)
      if (wasHolding) {
        stopForwardMovement();
        // If using callback, we need to notify parent to stop movement
        // The parent component should handle stopping via handleTouchEnd
        if (onTouchGesture && externalDirection !== null) {
          // Movement is controlled externally, parent will handle stop
          // We'll rely on the parent's touch end handling
        }
      }

      const maxSwipeTime = 300; // Maximum time for a quick swipe gesture (ms)

      // If it was a quick swipe up (not touch and hold), trigger a short forward movement
      if (!wasHolding && touchTime < maxSwipeTime && gestureDirection.current === "up") {
        e.preventDefault();
        if (onTouchGesture) {
          onTouchGesture("up");
          setTimeout(() => {
            // Stop after short movement - parent should handle this
            if (internalControls.handleTouchEnd && externalDirection === null) {
              internalControls.handleTouchEnd("up");
            }
          }, 200);
        } else if (internalControls.handleTouchStart && externalDirection === null) {
          internalControls.handleTouchStart("up");
          setTimeout(() => {
            if (internalControls.handleTouchEnd) {
              internalControls.handleTouchEnd("up");
            }
          }, 200);
        } else {
          setInternalIsAvatarMoving(true);
          setTimeout(() => {
            setInternalIsAvatarMoving(false);
          }, 200);
        }
      }

      // Reset gesture state
      touchStartPos.current = null;
      isGestureActive.current = false;
      gestureDirection.current = null;
    };

    const handleTouchCancel = () => {
      stopForwardMovement();
      touchStartPos.current = null;
      isGestureActive.current = false;
      gestureDirection.current = null;
    };

    // Add event listeners to the window (will work on the canvas area)
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });
    window.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      stopForwardMovement();
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [isMobile, internalControls, onTouchGesture, externalDirection, externalRotationDirection]);


  // Camera controller component that follows avatar
  // Camera always stays behind avatar, smoothly following rotation
  function CameraController({
    avatarPosition,
    avatarRotation,
    isMobile
  }: {
    avatarPosition: [number, number, number];
    avatarRotation: number;
    isMobile: boolean;
  }) {
    const { camera } = useThree();
    const targetPosition = useRef<THREE.Vector3>(new THREE.Vector3());
    const currentCameraPos = useRef<THREE.Vector3>(new THREE.Vector3());
    const initialized = useRef(false);
    const lastAvatarPos = useRef<[number, number, number]>(avatarPosition);
    const cameraRotation = useRef<number>(0); // Track camera's rotation (smoothly follows avatar)

    // Initialize camera position based on avatar position
    useEffect(() => {
      // Initialize camera rotation to match avatar rotation
      cameraRotation.current = avatarRotation;

      // Calculate camera position behind avatar
      // Mobile: increased zoom out for better view
      const offsetY = isMobile ? 4 : 3; // Height above avatar (slightly higher on mobile)
      const offsetDistance = isMobile ? 8 : 5; // Distance behind avatar (much further on mobile)

      // Calculate offset based on avatar rotation
      const cosAngle = Math.cos(avatarRotation);
      const sinAngle = Math.sin(avatarRotation);
      const rotatedOffsetX = -offsetDistance * sinAngle;
      const rotatedOffsetZ = -offsetDistance * cosAngle;

      const initialPos = new THREE.Vector3(
        avatarPosition[0] + rotatedOffsetX,
        avatarPosition[1] + offsetY,
        avatarPosition[2] + rotatedOffsetZ
      );

      camera.position.copy(initialPos);
      currentCameraPos.current.copy(initialPos);
      camera.lookAt(avatarPosition[0], avatarPosition[1] + 1, avatarPosition[2]);
      initialized.current = true;
    }, [avatarRotation, avatarPosition, isMobile]);

    useFrame(() => {
      if (initialized.current) {
        // Smoothly interpolate camera rotation to match avatar rotation
        // This creates smooth camera movement when avatar rotates left/right
        const currentNormalized = ((cameraRotation.current % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const targetNormalized = ((avatarRotation % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

        // Calculate shortest rotation path
        let diff = targetNormalized - currentNormalized;
        if (diff > Math.PI) diff -= 2 * Math.PI;
        if (diff < -Math.PI) diff += 2 * Math.PI;

        // Smooth interpolation - camera follows avatar rotation smoothly
        cameraRotation.current += diff * 0.15; // Smooth rotation speed

        // Normalize the result
        cameraRotation.current = ((cameraRotation.current % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

        // Calculate camera offset - ALWAYS positioned behind avatar
        // Use actual avatar rotation (not interpolated) to ensure camera is always behind
        // Mobile: increased zoom out for better view
        const offsetY = isMobile ? 4 : 3; // Height above avatar (slightly higher on mobile)
        const offsetDistance = isMobile ? 8 : 5; // Distance behind avatar (much further on mobile)

        // Rotate the offset based on avatar's actual facing direction
        // This ensures camera stays behind avatar when avatar turns
        const cosAngle = Math.cos(avatarRotation);
        const sinAngle = Math.sin(avatarRotation);

        // Apply rotation to offset (rotate around Y axis)
        // When avatar faces forward (angle=0, towards +Z), camera is behind at -Z
        // When avatar turns, camera rotates around avatar to stay behind
        // Behind means opposite direction of avatar's facing
        const rotatedOffsetX = -offsetDistance * sinAngle; // Behind = opposite of forward X
        const rotatedOffsetZ = -offsetDistance * cosAngle; // Behind = opposite of forward Z

        // Calculate target camera position (behind avatar)
        targetPosition.current.set(
          avatarPosition[0] + rotatedOffsetX,
          avatarPosition[1] + offsetY,
          avatarPosition[2] + rotatedOffsetZ
        );

        // Smoothly interpolate camera position - faster response
        currentCameraPos.current.lerp(targetPosition.current, 0.15);
        camera.position.copy(currentCameraPos.current);

        // Camera always looks at avatar position (focus on avatar)
        camera.lookAt(avatarPosition[0], avatarPosition[1] + 1, avatarPosition[2]);

        // Update last position
        lastAvatarPos.current = avatarPosition;
      }
    });

    return null;
  }

  return (
    <Canvas
      shadows
      gl={{ antialias: true, alpha: true }}
      style={{ width: "100%", height: "100%" }}
    >
      {/* Theme-based lighting - changes each time game starts */}
      <ambientLight intensity={themeConfig.ambientIntensity} />
      <directionalLight
        position={[10, 10, 5]}
        intensity={themeConfig.directionalIntensity}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      <pointLight position={[-10, 10, -10]} intensity={themeConfig.pointLightIntensity} color={themeConfig.pointLightColor} />

      {/* Camera with dynamic position - will be controlled by CameraController */}
      <PerspectiveCamera
        makeDefault
        position={[0, 12, 2]}
        fov={isMobile ? 75 : 65}
      />

      {/* Camera controller that follows avatar */}
      <CameraController
        avatarPosition={avatarPosition}
        avatarRotation={internalAvatarRotation}
        isMobile={isMobile}
      />

      {/* Sky sphere - theme color, visible when looking up */}
      <mesh>
        <sphereGeometry args={[200, 32, 32]} />
        <meshStandardMaterial 
          color={themeConfig.skyColor} 
          side={THREE.BackSide}
          fog={false}
        />
      </mesh>

      {/* Ground plane - theme color */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.5, 0]} receiveShadow>
        <planeGeometry args={[100, 100]} />
        <meshStandardMaterial color={themeConfig.groundColor} />
      </mesh>

      {/* Learning item buildings - grid layout */}
      <AlphabetGrid
        items={items}
        onItemClick={onItemClick}
        completedItems={completedItems}
        avatarPosition={avatarPosition || [0, 0, -20]}
        onBoxPositionUpdate={onBoxPositionsUpdate}
        shuffleKey={shuffleKey}
        hintedItemId={hintedItemId}
        isHintActive={isHintActive}
      />

      {/* 3D Avatar */}
      {avatarType && (
        <>
          <Avatar3D
            avatarType={avatarType}
            position={avatarPosition}
            targetPosition={avatarTarget}
            onReachTarget={onAvatarReachTarget}
            isWalking={avatarTarget !== null}
            isKeyboardMoving={isAvatarMoving}
            onRotationChange={undefined} // Don't update camera rotation from avatar movement
            rotation={avatarRotation}
          />
          
          {/* Red warning circle at bottom of avatar when follower is near */}
          {enableFollower && isGameActive && followerDistance < 8 && (
            <WarningCircle 
              position={[avatarPosition[0], 0.05, avatarPosition[2]]} 
              intensity={Math.max(0, 1 - (followerDistance / 8))} // More intense when closer
            />
          )}
        </>
      )}

      {/* Follower/Chaser - follows behind avatar */}
      {enableFollower && isGameActive && (
        <Avatar3D
          avatarType="robot" // Use robot as the chaser
          position={followerPosition}
          targetPosition={null}
          onReachTarget={() => {}}
          isWalking={true}
          isKeyboardMoving={true}
          rotation={followerRotation}
        />
      )}

      {/* Star reward animation */}
      {showStar && starPosition && (
        <StarReward position={starPosition} onComplete={onStarComplete} />
      )}

      {/* Object model when letter is clicked */}
      {showObject && objectPosition && objectModelPath && (
        <Suspense fallback={null}>
          <ObjectModel
            modelPath={objectModelPath}
            position={objectPosition}
            scale={1.5}
          />
        </Suspense>
      )}

      {/* Environment - theme preset (jungle/beach use envPreset) */}
      <Suspense fallback={null}>
        <Environment preset={themeConfig.envPreset ?? (sceneTheme === "jungle" ? "forest" : sceneTheme === "beach" ? "sunset" : sceneTheme)} />
      </Suspense>
    </Canvas>
  );
}

